import { Fragment } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useOperatorCompactRoute } from '@/utils/operatorCompact.js';

export default function PageHeaderCompact({
  title,
  description,
  breadcrumbs = [],
  showBreadcrumb = false,
  action,
  actions,
  children,
  className = '',
}) {
  const { isOperatorCompact } = useOperatorCompactRoute();
  const actionContent = actions ?? action ?? null;
  const hideTitleBlock = isOperatorCompact && !showBreadcrumb;

  if (hideTitleBlock && !actionContent && !children) {
    return null;
  }

  return (
    <header className={`flex flex-col ${hideTitleBlock ? 'gap-1' : 'gap-2'} ${className}`.trim()}>
      <div className={`flex flex-col gap-2 ${hideTitleBlock ? 'md:items-end' : 'md:flex-row md:items-start md:justify-between'}`}>
        <div className="min-w-0 flex-1">
          {showBreadcrumb && breadcrumbs.length > 0 ? (
            <nav aria-label="Breadcrumb" className="mb-1 text-xs">
              <ol className="flex flex-wrap items-center gap-1.5 text-[rgb(var(--muted))]">
                {breadcrumbs.map((crumb, index) => {
                  const isLast = index === breadcrumbs.length - 1;
                  const content = crumb.href ? (
                    <Link
                      to={crumb.href}
                      className="inline-flex items-center gap-1 rounded px-1 text-[rgb(var(--muted))] transition-colors hover:text-[rgb(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span>{crumb.label}</span>
                  );

                  return (
                    <Fragment key={`${crumb.label}-${index}`}>
                      <li
                        className={`inline-flex items-center gap-1.5 ${isLast ? 'font-medium text-[rgb(var(--fg))]' : ''}`}
                        aria-current={isLast ? 'page' : undefined}
                      >
                        {content}
                        {!isLast ? <ChevronRight size={12} aria-hidden="true" /> : null}
                      </li>
                    </Fragment>
                  );
                })}
              </ol>
            </nav>
          ) : null}

          {!hideTitleBlock ? (
            <div className="min-w-0">
              <h1 className="text-lg font-semibold tracking-tight text-[rgb(var(--fg))] md:text-[1.25rem]">
                {title}
              </h1>
              {description ? (
                <p className="mt-0.5 max-w-3xl text-xs leading-5 text-[rgba(var(--muted),0.95)] sm:text-sm">
                  {description}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        {actionContent ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2 self-start">
            {actionContent}
          </div>
        ) : null}
      </div>

      {children ? <div>{children}</div> : null}
    </header>
  );
}
